import React from 'react';
import { render, cleanup } from '@testing-library/react';
import { withWrapper } from '../testWrapper';
import { enableMocking, disableMocking } from '../mockApi';
import { captureComponentSnapshot, createLogCapture } from '../snapshotCapture';

function assert(condition, message) {
  if (!condition) throw new Error(message);
}

async function renderTest(element, assertions) {
  enableMocking();
  let snapshot = null;
  const logCapture = createLogCapture();
  try {
    const result = render(element);
    await assertions(result);
    snapshot = captureComponentSnapshot(result.container);
  } finally {
    cleanup();
    disableMocking();
    logCapture.restore();
  }
  return { snapshot, logs: logCapture.getLogs() };
}

export const componentTests = [
  {
    id: 'comp-notfound-1',
    name: 'NotFound page renders without crashing',
    run: async () => {
      const NotFound = require('../../Pages/Public/NotFound').default;
      return await renderTest(withWrapper(<NotFound />), async ({ container }) => {
        assert(container.firstChild !== null, 'NotFound should render something');
      });
    },
  },
  {
    id: 'comp-loader-1',
    name: 'Loader component renders',
    run: async () => {
      const Loader = require('../../Components/Loader').default;
      return await renderTest(<Loader />, async ({ container }) => {
        assert(container.firstChild !== null, 'Loader should render');
      });
    },
  },
  {
    id: 'comp-upgraded-loader-1',
    name: 'UpgradedLoader component renders',
    run: async () => {
      const UpgradedLoader = require('../../Components/UpgradedLoader').default;
      return await renderTest(<UpgradedLoader />, async ({ container }) => {
        assert(container.firstChild !== null, 'UpgradedLoader should render');
      });
    },
  },
  {
    id: 'comp-login-1',
    name: 'Login page renders email input',
    run: async () => {
      const Login = require('../../Pages/Public/Login').default;
      return await renderTest(withWrapper(<Login />, '/login'), async ({ container }) => {
        const emailInput = container.querySelector('input[type="email"], input[name="email"]');
        const anyInput = container.querySelector('input');
        assert(emailInput !== null || anyInput !== null, 'Login should have at least one input');
      });
    },
  },
  {
    id: 'comp-login-2',
    name: 'Login page renders password input',
    run: async () => {
      const Login = require('../../Pages/Public/Login').default;
      return await renderTest(withWrapper(<Login />, '/login'), async ({ container }) => {
        const pwdInput = container.querySelector('input[type="password"]');
        assert(pwdInput !== null, 'Login should have a password input');
      });
    },
  },
  {
    id: 'comp-login-3',
    name: 'Login page renders a submit button',
    run: async () => {
      const Login = require('../../Pages/Public/Login').default;
      return await renderTest(withWrapper(<Login />, '/login'), async ({ container }) => {
        const btn = container.querySelector('button');
        assert(btn !== null, 'Login should have at least one button');
      });
    },
  },
  {
    id: 'comp-register-1',
    name: 'Register page renders without crashing',
    run: async () => {
      const Register = require('../../Pages/Public/Register').default;
      return await renderTest(withWrapper(<Register />, '/register'), async ({ container }) => {
        assert(container.firstChild !== null, 'Register should render something');
      });
    },
  },
  {
    id: 'comp-register-2',
    name: 'Register page renders password input',
    run: async () => {
      const Register = require('../../Pages/Public/Register').default;
      return await renderTest(withWrapper(<Register />, '/register'), async ({ container }) => {
        const pwdInput = container.querySelector('input[type="password"]');
        assert(pwdInput !== null, 'Register should have a password input');
      });
    },
  },
  {
    id: 'comp-project-card-1',
    name: 'ProjectCard renders project name',
    run: async () => {
      const ProjectCard = require('../../Components/Projects/ProjectCard').default;
      const mockProject = {
        uuid: 'p1',
        name: 'Alpha Research Project',
        description: 'A test project description',
        completed_steps: [],
      };
      return await renderTest(withWrapper(<ProjectCard project={mockProject} />), async ({ container }) => {
        assert(container.textContent.includes('Alpha Research Project'), 'ProjectCard should show project name');
      });
    },
  },
  {
    id: 'comp-project-card-2',
    name: 'ProjectCard has view button',
    run: async () => {
      const ProjectCard = require('../../Components/Projects/ProjectCard').default;
      const mockProject = {
        uuid: 'p2',
        name: 'Beta Project',
        description: 'Another project',
        completed_steps: ['data_collection'],
      };
      return await renderTest(withWrapper(<ProjectCard project={mockProject} />), async ({ container }) => {
        const btn = container.querySelector('[data-testid="project-card-view-button"], button');
        assert(btn !== null, 'ProjectCard should have a button');
      });
    },
  },
  {
    id: 'comp-section-header-1',
    name: 'SectionHeader renders with title text',
    run: async () => {
      const SectionHeader = require('../../Components/GeneralComponents/SectionHeader').default;
      return await renderTest(<SectionHeader title="Test Section Title" />, async ({ container }) => {
        assert(container.textContent.includes('Test Section Title'), 'SectionHeader should show title');
      });
    },
  },
  {
    id: 'comp-confirmation-modal-1',
    name: 'ConfirmationModal renders (closed state)',
    run: async () => {
      const ConfirmationModal = require('../../Components/ConfirmationModal').default;
      return await renderTest(
        withWrapper(
          <ConfirmationModal
            open={false}
            setOpen={() => {}}
            onConfirm={() => {}}
            title="Delete item"
            message="Are you sure you want to delete this?"
          />
        ),
        async ({ container }) => {
          assert(container !== null, 'ConfirmationModal should not crash');
        }
      );
    },
  },
  {
    id: 'comp-success-popup-1',
    name: 'SuccessPopup renders with message',
    run: async () => {
      const SuccessPopup = require('../../Components/PopupNotifications/SuccessPopup').default;
      return await renderTest(
        <SuccessPopup message="Operation successful!" show={true} />,
        async ({ container }) => {
          assert(container !== null, 'SuccessPopup should not crash');
        }
      );
    },
  },
  {
    id: 'comp-error-popup-1',
    name: 'ErrorPopup renders with message',
    run: async () => {
      const ErrorPopup = require('../../Components/PopupNotifications/ErrorPopup').default;
      return await renderTest(
        <ErrorPopup message="Something went wrong" show={true} />,
        async ({ container }) => {
          assert(container !== null, 'ErrorPopup should not crash');
        }
      );
    },
  },
];
